import { useMemo, useRef, useState } from 'react';
import { FormModal } from '@/components/shared/form-modal';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { useCurrentRegister, usePackageOptions, usePaymentMethods, usePendingPayments, useSubscriptionInfo } from '../api';
import { CheckoutForm, type CheckoutTarget } from './checkout-form';

/**
 * Carrega o alvo do recebimento (atendimento pendente ou assinatura), os métodos e o caixa;
 * o formulário só monta quando tudo está pronto. A chave de idempotência nasce ao abrir.
 */
export function CheckoutModal({
  appointmentId,
  subscriptionId,
  defaultBranchId,
  onClose,
}: {
  appointmentId: string | null;
  subscriptionId: string | null;
  defaultBranchId: string | null;
  onClose: () => void;
}) {
  const idempotencyKey = useRef(crypto.randomUUID()).current;
  const methods = usePaymentMethods();
  const pending = usePendingPayments({ pageSize: 100, sort: 'completedAt', order: 'asc' }, !!appointmentId);
  const sub = useSubscriptionInfo(subscriptionId);
  const found = useRef<CheckoutTarget | null>(null);

  if (!found.current) {
    if (appointmentId) {
      const p = pending.data?.data.find((x) => x.appointmentId === appointmentId);
      if (p) found.current = { kind: 'appointment', appointmentId: p.appointmentId, customerId: p.customerId, customerName: p.customerName, title: p.serviceName, subtitle: `Profissional: ${p.barberName}`, branchId: p.branchId };
    } else if (sub.data) {
      const s = sub.data;
      found.current = { kind: 'subscription', subscriptionId: s.subscriptionId, customerId: s.customerId, customerName: s.customerName, title: `Assinatura ${s.planName}`, subtitle: undefined, branchId: defaultBranchId };
    }
  }
  const target = found.current;
  const branchId = target?.branchId ?? defaultBranchId;
  const register = useCurrentRegister(branchId);
  const pkgs = usePackageOptions(target?.kind === 'appointment' ? (target.appointmentId ?? null) : null);

  const loading = methods.isLoading || (appointmentId ? pending.isLoading : sub.isLoading) || (!!target && register.isLoading);
  const error = methods.error ?? (appointmentId ? pending.error : sub.error) ?? register.error;
  const activeMethods = useMemo(() => (methods.data ?? []).filter((m) => m.active), [methods.data]);

  const retry = () => {
    void methods.refetch();
    void pending.refetch();
    void sub.refetch();
    void register.refetch();
  };

  let body;
  if (loading) body = <LoadingState />;
  else if (error) body = <ErrorState error={error} onRetry={retry} />;
  else if (!target) {
    body = (
      <div role="alert" className="grid gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-5 text-sm">
        <p className="font-medium">Não foi possível receber este item.</p>
        <p className="text-muted-foreground">
          {appointmentId
            ? 'O atendimento não está pendente de recebimento. Ele pode já ter sido pago, não estar concluído ou ter sido removido.'
            : 'Não encontramos a assinatura informada.'}
        </p>
        <div>
          <Button variant="outline" size="sm" onClick={onClose}>
            Voltar ao caixa
          </Button>
        </div>
      </div>
    );
  } else if (activeMethods.length === 0) {
    body = <ErrorState error={new Error('Nenhum método de pagamento ativo.')} onRetry={retry} />;
  } else {
    body = (
      <CheckoutForm
        target={target}
        branchId={branchId}
        register={register.data?.register ?? null}
        methods={activeMethods}
        packages={pkgs.data ?? []}
        idempotencyKey={idempotencyKey}
        onClose={onClose}
      />
    );
  }

  // O formulário traz o próprio FormModal (com rodapé); os demais estados usam um modal simples.
  if (target && !loading && !error && activeMethods.length > 0) return body;
  return (
    <FormModal open onOpenChange={(v) => !v && onClose()} size="lg" title="Receber">
      {body}
    </FormModal>
  );
}
